import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/client';
import { Alert, Button } from '../../components';
import { detectSubdomain, getCompanySlug } from '../../utils/subdomain';
import { useHostContext } from '../../hooks/useHostContext';
import { RIDER_THEME } from '../rider/RiderV2Constants';
import { PORTAL_LOGIN_CONFIG, PORTAL_THEMES } from './portalLoginConfig';

function resolveText(value, company, slug) {
  if (typeof value === 'function') return value(company, slug);
  return value;
}

export default function PortalLoginPage({ portal }) {
  const config = PORTAL_LOGIN_CONFIG[portal];
  const theme = PORTAL_THEMES[portal];
  const { slug: paramSlug } = useParams();
  const hostCtx = useHostContext();
  const slug = paramSlug || hostCtx.companySlug || getCompanySlug() || detectSubdomain();
  const [company, setCompany] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(!!config.needsCompany);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!config.needsCompany) {
      setCompanyLoading(false);
      return;
    }
    if (!slug || slug === 'admin') {
      setCompanyLoading(false);
      return;
    }
    setCompanyLoading(true);
    api.getCompanyBySlug(slug)
      .then((d) => { if (d.name) setCompany(d); else setError('الشركة غير موجودة'); })
      .catch(() => setError('فشل تحميل معلومات الشركة'))
      .finally(() => setCompanyLoading(false));
  }, [slug, config.needsCompany]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!username.trim() || !password.trim()) {
      setError('يرجى إدخال اسم المستخدم وكلمة المرور');
      return;
    }
    setLoading(true);
    try {
      const loginSlug = config.needsCompany && slug && slug !== 'admin' ? slug : undefined;
      const data = await api.login(username, password, loginSlug);
      if (config.requiredType && data.user.type !== config.requiredType) {
        setError(config.typeError);
        return;
      }
      login(data.token, data.user);
      const target = config.resolvePostLogin(data.user, hostCtx, slug);
      if (target) navigate(target);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const brandColor = company?.brand_color || '#3E2C6E';
  const title = resolveText(config.title, company, slug);
  const subtitle = resolveText(config.subtitle, company, slug);
  const logoUrl = config.logoField
    ? (company?.[config.logoField] || company?.logo_url)
    : company?.logo_url;

  if (config.needsCompany && !companyLoading && portal === 'rider' && company?.riders_enabled === 0) {
    return (
      <div style={{
        minHeight: '100vh', background: RIDER_THEME.bg, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 24, direction: 'rtl',
      }}>
        <div style={{
          maxWidth: 400, width: '100%', background: RIDER_THEME.card, borderRadius: 16,
          padding: 32, textAlign: 'center', border: `1px solid ${RIDER_THEME.border}`,
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🚫</div>
          <h2 style={{ color: RIDER_THEME.text, margin: '0 0 8px', fontSize: 18 }}>تطبيق المندوبين غير متاح</h2>
          <p style={{ color: RIDER_THEME.textMid, margin: 0, fontSize: 14, lineHeight: 1.6 }}>
            تم تعطيل بوابة المندوبين لهذه الشركة. تواصل مع إدارة الشركة للمزيد من المعلومات.
          </p>
        </div>
      </div>
    );
  }

  if (theme.dark) {
    return (
      <div style={{
        minHeight: '100vh', background: RIDER_THEME.bg, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 20,
      }}>
        <div style={{
          background: RIDER_THEME.card, border: '1px solid ' + RIDER_THEME.border,
          borderRadius: 24, padding: 32, maxWidth: 380, width: '100%',
        }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            {logoUrl ? (
              <img src={logoUrl} alt="" style={{ width: 64, height: 64, objectFit: 'contain', marginBottom: 8 }} />
            ) : (
              <div style={{ fontSize: 48, marginBottom: 8 }}>{config.fallbackIcon}</div>
            )}
            <div style={{ fontSize: 24, fontWeight: 800, color: RIDER_THEME.accent }}>{title}</div>
            <div style={{ fontSize: 14, color: RIDER_THEME.textMid, marginTop: 4 }}>{subtitle}</div>
          </div>
          {companyLoading ? (
            <div style={{ textAlign: 'center', padding: 20, color: RIDER_THEME.textMid }}>جاري التحميل...</div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {error && (
                <div data-testid="login-error" style={{
                  background: RIDER_THEME.red + '18', border: '1px solid ' + RIDER_THEME.red + '44',
                  color: RIDER_THEME.red, borderRadius: 12, padding: '10px 14px', fontSize: 13, textAlign: 'center',
                }}>{error}</div>
              )}
              <LoginField label="اسم المستخدم" value={username} onChange={setUsername} type="text" dark />
              <LoginField label="كلمة المرور" value={password} onChange={setPassword} type="password" dark />
              <button type="submit" data-testid="login-submit" disabled={loading} style={{
                width: '100%', padding: 14, marginTop: 4,
                background: RIDER_THEME.accent, border: 'none', color: '#000',
                borderRadius: 12, fontWeight: 700, fontSize: 15, cursor: 'pointer',
                opacity: loading ? 0.6 : 1,
              }}>
                {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  const pageStyle = typeof theme.page === 'function'
    ? theme.page(brandColor)
    : theme.page;

  return (
    <div style={{ ...lightStyles.container, ...pageStyle }}>
      <div style={{ ...lightStyles.card, ...theme.card }}>
        <div style={lightStyles.logoWrap}>
          {logoUrl ? (
            <img src={logoUrl} alt={title} style={lightStyles.logoImg} />
          ) : (
            <span style={{ fontSize: 48 }}>{config.fallbackIcon}</span>
          )}
        </div>
        <h1 data-testid="login-title" style={{
          ...lightStyles.title,
          ...(portal === 'merchant' ? lightStyles.merchantTitle : {}),
          color: typeof theme.titleColor === 'function' ? theme.titleColor(brandColor) : theme.titleColor,
        }}>{title}</h1>
        {subtitle && (
          <p style={{
            ...lightStyles.subtitle,
            ...(portal === 'merchant' ? lightStyles.merchantSubtitle : {}),
          }}>{subtitle}</p>
        )}

        {companyLoading ? (
          <p style={{ textAlign: 'center', color: '#999' }}>جاري التحميل...</p>
        ) : (
          <>
            {error && <Alert type="error" data-testid="login-error">{error}</Alert>}
            <form onSubmit={handleSubmit} style={lightStyles.form} data-testid="login-form">
              <LoginField label="اسم المستخدم" value={username} onChange={setUsername} type="text" />
              <LoginField label="كلمة المرور" value={password} onChange={setPassword} type="password" />
              <Button type="submit" variant="primary" size="lg" fullWidth loading={loading} style={{ marginTop: 8 }} data-testid="login-submit">
                {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function LoginField({ label, value, onChange, type, dark }) {
  if (dark) {
    return (
      <div>
        <label style={{ display: 'block', color: RIDER_THEME.text, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{label}</label>
        <input
          type={type} value={value} onChange={(e) => onChange(e.target.value)}
          data-testid={type === 'password' ? 'login-password' : 'login-username'}
          autoComplete={type === 'password' ? 'current-password' : 'username'}
          placeholder={type === 'password' ? 'أدخل كلمة المرور' : 'أدخل اسم المستخدم'}
          dir={type === 'text' ? 'auto' : undefined}
          style={{
            width: '100%', padding: '12px 14px', boxSizing: 'border-box',
            background: RIDER_THEME.surface, border: '1px solid ' + RIDER_THEME.border, borderRadius: 12,
            color: RIDER_THEME.text, fontSize: 14, outline: 'none',
          }}
        />
      </div>
    );
  }
  return (
    <div style={lightStyles.inputGroup}>
      <label style={lightStyles.label}>{label}</label>
      <input
        style={lightStyles.input}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={type === 'password' ? 'login-password' : 'login-username'}
        autoComplete={type === 'password' ? 'current-password' : 'username'}
        placeholder={type === 'password' ? 'أدخل كلمة المرور' : 'أدخل اسم المستخدم'}
        dir={type === 'text' ? 'auto' : undefined}
      />
    </div>
  );
}

const lightStyles = {
  container: {
    display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
  },
  card: {
    backgroundColor: 'white', borderRadius: 'var(--radius-lg)',
    padding: 'var(--sp-4xl)', width: '100%', maxWidth: 380,
    boxShadow: 'var(--shadow-xl)', textAlign: 'center',
  },
  logoWrap: { marginBottom: 12, display: 'flex', justifyContent: 'center' },
  logoImg: { width: 80, height: 80, objectFit: 'contain' },
  title: { fontSize: 'var(--font-size-xl)', margin: '0 0 4px 0' },
  merchantTitle: { fontSize: '22px', fontWeight: 800, lineHeight: 1.35, margin: '0 0 6px 0' },
  subtitle: { fontSize: 'var(--font-size-base)', color: 'var(--color-text-secondary)', margin: '0 0 24px 0' },
  merchantSubtitle: { fontSize: '13px', color: '#888', margin: '0 0 24px 0', fontWeight: 500 },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  inputGroup: { textAlign: 'right' },
  label: { display: 'block', fontSize: 'var(--font-size-base)', color: 'var(--color-text)', marginBottom: 6, fontWeight: 600 },
  input: {
    width: '100%', padding: '10px 12px',
    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-base)', boxSizing: 'border-box',
  },
};